/**
 * useAgentInbox.fetchers — chat-inbox slug fetchers.
 *
 * Gate 4 of MO-FLEET-IDENTITY-CONVERGENCE-V1 (doc `0d71c5c0`) routes the
 * Commander Bridge chat surface through the comms-owned `v_chat_inbox`
 * view (SL slug `/v1/views/chat-inbox`, ADR-056 view-proxy). The view
 * does the work the FE used to do client-side:
 *   - identity scoping via `auth.uid() → authorized_operators.principal_label`
 *     (no `'commander'` literal anywhere on the FE)
 *   - lane derivation from `(kind, signal_type, target IS NULL)` against
 *     the `signal_type='message'` allowlist (Gate 2 §3.1 sub-step 2.B)
 *   - redaction filter (Comms House Rules v5 §8)
 *   - session_id resolution from row column + payload/context fallbacks
 *
 * Pillar 3 migration: reads now go through `viewGetTyped` with the
 * CODEGEN `ChatInboxEnvelope` wire envelope (SL slug `chat-inbox`,
 * generated from the SL openapi SSoT per FE KPI #2) per
 * ADR-CROSS-SYSTEM-CONTRACTS-V1 v4 §2.2. Drift produces a
 * `ContractMismatchError` which react-query surfaces as `query.error`
 * (downstream hook reads `payloadError`-style branches if needed; the
 * existing `useAgentInbox` consumer renders the raw `query.error`).
 *
 * Two fetchers split for backwards-compatible call shape:
 *   - `fetchInbox` for the live tail (latest 50, refetched every 3s)
 *   - `fetchInboxPage` for paginated scrollback
 */

import { viewGetTyped } from '@appic/lib/http/viewGetTyped'
import { ChatInboxEnvelope } from '@appic/lib/wire/generated/schemas.generated'
import { createLogger } from '@appic/lib/utils/logger'
import type { AgentSignal } from './useAgentInbox.types'

const log = createLogger('useAgentInbox.fetchers')

export function sortInboxChronological(messages: AgentSignal[]): AgentSignal[] {
  return [...messages].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  )
}

/**
 * Dev-only structured trace of chat-inbox fetches. BUG-FE-BRIDGE-AGENT-CHAT-
 * EMPTY-2026-05-24 root-cause hardening: an empty `data[]` from `chat-inbox`
 * can come from (a) lane/agent filter combo that excludes everything, (b)
 * the operator legitimately has zero signals, or (c) server-side scope
 * (auth.uid()) returns no rows. Without the call site logging the filter
 * args + page size, the operator can't distinguish silent degradation from
 * a working empty inbox (D-33 R1). Dev-only so production console stays
 * clean; the diagnostic empty-state in AgentInbox.tsx is the prod surface.
 */
function tracePage(label: string, filters: Record<string, string>, size: number): void {
  if (import.meta.env?.DEV) {
    log.debug(label, { filters, size })
  }
}

function buildChatInboxFilters(
  agent: string | undefined,
  sessionFilter: string | null | undefined,
  lane: string | null | undefined,
  before: string | undefined,
): Record<string, string> {
  const filters: Record<string, string> = {}
  // Per-persona room scope: filter by the persona as a CONVERSATION PARTY in
  // BOTH directions (`source` OR `target`), NOT by `agent_name`.
  //
  // `agent_name` is the message AUTHOR. The operator's own sends
  // (`POST /v1/commander/inbox/send`, commander→<persona>) are written with
  // `agent_name = NULL` (verified: `source=commander, target=frontend,
  // kind=reply, signal_type=message, agent_name=NULL`), and the
  // `get_chat_inbox` RPC scopes with `ag.agent_name = p_agent_name`. So an
  // `agent_name=<persona>` filter SILENTLY DROPS every commander→persona
  // message — the operator's freshly-sent row is excluded on the post-send
  // refetch and the optimistic bubble vanishes to an empty "No signals for
  // this filter" pane (BUG-FE-BRIDGE-CHAT-OPERATOR-SEND-VANISHES). It also
  // misses agent→commander replies authored with a null `agent_name`.
  //
  // `or` is an allowlisted residual PostgREST filter (`_CHAT_INBOX_FILTERS`
  // lists `source`/`target`/`or`). The proxy maps only
  // `agent_name`/`lane`/`created_at` to RPC params
  // (view_proxy_rpc.build_rpc_params); everything else is applied via
  // `apply_filters` → postgrest-py `.or_(value)` ON the `get_chat_inbox`
  // result set — so omitting `agent_name` lets the RPC return the operator's
  // full konversation feed, then the `or` narrows it to the persona's
  // both-direction traffic (commander→persona included). NEVER-DARK: the
  // just-sent row is the newest konversation row, so it survives the
  // lane+limit window and the residual filter, and renders as the real
  // persisted message after refetch.
  //
  // CRITICAL — supply the INNER logic ONLY (no surrounding parens).
  // postgrest-py `or_(filters)` wraps the value ITSELF
  // (`params.add("or", f"({filters})")`, base_request_builder.py:422), so a
  // pre-parenthesized `(source.eq…,target.eq…)` double-wraps to `or=((…))`
  // → PostgREST `PGRST100 failed to parse logic tree` and the whole chat
  // read errors dark (BUG-FE-BRIDGE-CHAT-OR-FILTER-DOUBLE-PAREN). The bare
  // comma-separated form below serializes to the single-paren-valid
  // `or=(source.eq.<p>,target.eq.<p>)` after the builder's one wrap.
  if (agent) filters.or = `source.eq.${agent},target.eq.${agent}`
  if (sessionFilter) filters.session_id = `eq.${sessionFilter}`
  // `lane` maps to the RPC's `p_lane`, and the proxy passes it UNSTRIPPED
  // (build_rpc_params: `rpc_args["p_lane"] = val`). `get_chat_inbox` compares
  // the derived lane against the bare value (`derived_lane = p_lane`), so an
  // `eq.` prefix here yields `p_lane='eq.konversation'` → 0 rows → the whole
  // konversation room rendered server-empty. Send the BARE lane (unlike
  // `session_id`, which is a residual filter and keeps its `eq.` operator).
  if (lane) filters.lane = lane
  if (before) filters.created_at = `lt.${before}`
  return filters
}

export async function fetchInbox(
  agent?: string,
  sessionFilter?: string | null,
  lane?: string | null,
  // React Query's per-query AbortSignal — MUST be threaded to the fetch so a
  // refetch/unmount cancellation aborts AT the request and the resulting
  // AbortError is converted to a (handled) QueryCancelledError in
  // serviceLayerClient. Omitting it (the prior bug) left the abort to escape
  // as a raw DOMException → an UNHANDLED rejection flood on the bridge agent
  // screens (BUG-2026-06-15-FTJP6/W355J: ~87 "signal is aborted without
  // reason" on a 3s refetchInterval × parallel queries).
  signal?: AbortSignal,
): Promise<AgentSignal[]> {
  const filters = buildChatInboxFilters(agent, sessionFilter, lane, undefined)
  const res = await viewGetTyped(
    'chat-inbox',
    ChatInboxEnvelope,
    {
      filters,
      limit: 50,
      order: 'created_at.desc',
      signal,
    },
  )
  tracePage('fetchInbox', filters, res.data.length)
  return sortInboxChronological(res.data as AgentSignal[])
}

/**
 * §F1 paginated scrollback. PostgREST returns signals strictly older than
 * the cursor when `created_at=lt.<iso>` is set; ordering is `desc` so the
 * pagination is "give me the next 50 below this timestamp." We sort
 * chronologically client-side so the virtualizer renders oldest-first.
 */
export async function fetchInboxPage(
  agent: string | undefined,
  sessionFilter: string | null | undefined,
  before: string | undefined,
  lane?: string | null,
  // See fetchInbox: thread React Query's AbortSignal so cancellation aborts at
  // the request (handled QueryCancelledError) instead of escaping as a raw
  // unhandled AbortError (BUG-2026-06-15-FTJP6/W355J).
  signal?: AbortSignal,
): Promise<AgentSignal[]> {
  const filters = buildChatInboxFilters(agent, sessionFilter, lane, before)
  const res = await viewGetTyped(
    'chat-inbox',
    ChatInboxEnvelope,
    {
      filters,
      limit: 50,
      order: 'created_at.desc',
      signal,
    },
  )
  tracePage('fetchInboxPage', filters, res.data.length)
  return sortInboxChronological(res.data as AgentSignal[])
}
